import { useNavigate } from "react-router-dom";
import SectionLabel from "@/components/base/SectionLabel";
import { dxAdvisoryData } from "@/mocks/dxAdvisoryData";

export default function MonthlyServiceSection() {
  const navigate = useNavigate();

  const scrollToContact = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="monthly-service" className="py-20 md:py-28 bg-primary-500/[0.03]">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-14 md:mb-20 reveal">
          <SectionLabel label={dxAdvisoryData.monthlyService.label} className="mb-3" />
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900">
            {dxAdvisoryData.monthlyService.title}
          </h2>
        </div>

        {/* Desktop: Circular layout - most important */}
        <div className="hidden lg:block relative reveal">
          <div className="flex justify-center mb-14">
            <div className="relative w-[520px] h-[520px]">
              {/* Center - stays fixed */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-primary-500 text-background-50 flex flex-col items-center justify-center z-10">
                <i className="ri-refresh-line text-2xl mb-1" />
                <span className="text-sm font-bold text-center leading-tight font-heading">
                  {dxAdvisoryData.monthlyService.centerLabel}
                </span>
              </div>

              {/* Ferris wheel: outer orbits clockwise, each block counter-rotates to stay upright */}
              <div className="absolute inset-0 animate-ferris-orbit">
              {dxAdvisoryData.monthlyService.items.map((item, idx) => {
                const angle = (idx * 60 - 90) * (Math.PI / 180);
                const radius = 210;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <div
                    key={item.num}
                    className="absolute"
                    style={{
                      top: `calc(50% + ${y}px)`,
                      left: `calc(50% + ${x}px)`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <div className="animate-ferris-counter w-40 text-center">
                      <div className="w-12 h-12 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-2.5">
                        <i className={`${item.icon} text-primary-500 text-xl`} />
                      </div>
                      <p className="text-xs font-bold text-foreground-900 mb-1.5 font-heading">
                        <span className="text-primary-500 block text-[10px] font-label mb-0.5">{item.num}</span>
                        {item.title}
                      </p>
                      <p className="text-[11px] text-foreground-600 leading-relaxed px-1">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: 2-column grid */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-12">
          {dxAdvisoryData.monthlyService.items.map((item, idx) => (
            <div
              key={item.num}
              className="reveal p-5 rounded-lg bg-background-50 border border-background-200/70"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-primary-500 text-lg`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-900 mb-1 font-heading">
                    <span className="text-primary-500 mr-1">{item.num}</span>
                    {item.title}
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cycle phases ribbon (desktop) */}
        <div className="hidden lg:flex justify-center items-center gap-3 reveal">
          {dxAdvisoryData.monthlyService.cyclePhases.map((phase, idx) => (
            <span key={idx} className="flex items-center gap-3">
              <span className="px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium font-label">
                {phase}
              </span>
              {idx < dxAdvisoryData.monthlyService.cyclePhases.length - 1 && (
                <i className="ri-arrow-right-line text-primary-400" />
              )}
            </span>
          ))}
        </div>

        {/* Integrated Deliverables */}
        <div className="mt-16 md:mt-20 reveal">
          <h3 className="text-lg md:text-xl font-bold text-foreground-900 text-center mb-8 md:mb-10 font-heading">
            {dxAdvisoryData.monthlyService.deliverables.title}
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto items-center">
            {/* Left: List */}
            <div className="space-y-3 md:space-y-4">
              {dxAdvisoryData.monthlyService.deliverables.items.map((item, idx) => (
                <div
                  key={idx}
                  className="reveal flex items-center gap-3 md:gap-4"
                  style={{ transitionDelay: `${idx * 80}ms` }}
                >
                  <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-500`} />
                  </div>
                  <span className="text-sm md:text-base text-foreground-800">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Right: Report mockup */}
            <div className="reveal relative rounded-lg overflow-hidden bg-background-50 border border-background-200/70 p-5 md:p-7">
              <div className="space-y-4">
                <div className="border-b border-background-200/70 pb-3">
                  <p className="text-xs font-medium text-primary-500 font-label mb-1">月次レポート</p>
                  <div className="h-3.5 bg-background-200 rounded w-3/4 mb-1.5" />
                  <div className="h-3 bg-background-200 rounded w-1/2" />
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-primary-500 text-[11px]" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <div className="h-3 bg-background-200 rounded w-5/6" />
                      <div className="h-3 bg-background-200 rounded w-2/3" />
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-primary-500 text-[11px]" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <div className="h-3 bg-background-200 rounded w-4/5" />
                      <div className="h-3 bg-background-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-right-line text-primary-500 text-[11px]" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <div className="h-3 bg-background-200 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-3 right-3">
                <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] font-medium font-label">
                  A4一枚
                </span>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-foreground-500 mt-4">
            イメージ：月次レポート（実際の機密情報は掲載しません）
          </p>
        </div>

        {/* Mid CTA */}
        <div className="mt-14 md:mt-16 reveal">
          <div className="max-w-2xl mx-auto text-center bg-background-50 border border-primary-200/60 rounded-lg p-7 md:p-9">
            <h3 className="text-lg md:text-xl font-bold text-foreground-900 mb-5 font-heading">
              {dxAdvisoryData.monthlyService.midCta.text}
            </h3>
            <button
              onClick={scrollToContact}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {dxAdvisoryData.monthlyService.midCta.button}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}